import { AfterViewInit, Component, computed, ElementRef, inject, OnInit, output, signal, viewChild, ChangeDetectionStrategy } from '@angular/core';
import { CommonButtonRowWidget, CommonDateTimeFieldWidget, CommonMessageWidget, CommonSelectFieldWidget, CommonTextFieldWidget, CommonUrlFieldWidget } from '@eforall/common';
import { DialogService } from '@eforall/dashboard';
import { EventTypeId, StaffData, Venue } from '@interfaces';
import { StaffSelectorWidget } from 'ui/widget/staff-selector';
import { AppService } from '../../../../../services';
import { EventEditorFormService, EventEditorFormValue } from './event-editor-form.service';
import { VenueButtonPart } from '../../venue/venue-button/venue-button.part';


@Component({
	selector: 'event-dialog-editor-part',
	standalone: true,
	imports: [
		CommonTextFieldWidget,
		CommonUrlFieldWidget,
		CommonButtonRowWidget,
		CommonMessageWidget,
		CommonDateTimeFieldWidget,
		CommonSelectFieldWidget,
		StaffSelectorWidget,
		VenueButtonPart,
	],
	changeDetection: ChangeDetectionStrategy.Eager,
	templateUrl: './event-editor.part.html'
})

export class EventDialogEditorPart implements OnInit, AfterViewInit {

	public app = inject(AppService);
	private dialogService = inject(DialogService);

	public form = new EventEditorFormService();
	public eventToEdit = computed(() =>
		this.form.eventDialogDataService.event()
	);

	public sessionEventTypeId = EventTypeId.AccSession;

	public eventFormSaveBtn = viewChild.required<ElementRef<HTMLButtonElement>>('eventFormSaveBtn');
	public staffData = signal<StaffData[]>([]);

	public closeDialog = output();


	async ngOnInit() {
		this.form.setValues(this.eventToEdit());
		this.staffData.set(this.app.data().staff);
	}

	ngAfterViewInit() {
		this.form.form.setSubmitButton(
			this.eventFormSaveBtn(),
			async (values: EventEditorFormValue, changed: string[]) => {
				console.log('Submitting event edit form with values:', values, 'and changed fields:', changed);
				const eventToEdit = this.eventToEdit();
				if (eventToEdit) {
					if (values.toolSelection === 'automatic' || !eventToEdit.eventType.toolNameLabelKey) {
						values.toolStartUTC = values.startUTC - (1 * 24 * 60 * 60);
						values.toolEndUTC = values.endUTC + (1 * 24 * 60 * 60);
					}

					if (!values.toolStartUTC || !values.toolEndUTC) {
						throw new Error('Tool start and end dates are missing');
					}

					const venueId = this.form.values().venueId;
					if (values.venue === 'In Person' && !venueId) {
						throw new Error('Venue must be selected for In Person events');
					}

					await this.form.eventDialogDataService.updateEvent(eventToEdit.eventId, {
						startUTC: values.startUTC,
						endUTC: values.endUTC,
						toolStartUTC: values.toolStartUTC!,
						toolEndUTC: values.toolEndUTC!,
						link: values.link,
						publicNotes: values.publicNotes,
						hostPersonId: this.form.hostPerson().personId,
						venueId: values.venue === 'In Person' ? venueId : undefined,
					});

					this.closeDialog.emit();
				}
			}
		);
	}


	setInitialValuesforEdit() {
		this.form.setValues(this.eventToEdit());
	}

	public async deleteEvent() {
		const eventId = this.eventToEdit()?.eventId;
		if (eventId) {
			if (!await this.dialogService.confirm(`Are you sure you want to delete this event #${eventId}?`)) return;

			const results = await this.form.eventDialogDataService.deleteEvent(eventId);
			if (results.reasons.length) {
				await this.dialogService.showMessage('Delete Event', 'Cannot delete event for the following reasons:', undefined, results.reasons);
			}
			else this.closeDialog.emit();
		}
	}

	/**
 * Event raised when a venue is selected from the venue dialog.
 */
	public onVenueSelection(venue: { venueId: number, displayedName: string }) {
		this.form.values.set({...this.form.values(), venueId: venue.venueId, venueDisplayedName: venue.displayedName});
	}
}